import { useState } from "react";
import { useAccess } from "../../../auth/useAccess";
import {
  AGENT_TAB_IDS,
  AGENT_TAB_LABELS,
  AgentRosterList,
  AgentsAdminPanel,
  useAgentRoster,
  type AgentTab,
} from "./AgentsAdminPanel";
// The studio's panels are the Copilot workspace's neighbours and wear its
// stylesheets: the knowledge-scope popover the Setup form opens lives in
// copilot.css.
import "../../copilot/copilot.css";
import "../../copilot/copilot-settings.css";

/**
 * Agents, as the canvas's second Copilot-administration tab.
 *
 * It was "Thinking models", and for one release it was thinking models AND
 * agents behind one strip of sub-tabs — which is how the duplication became
 * obvious: both asked for a name, a description and standing instructions, an
 * administrator filled in the same three fields twice, and the composer then
 * let you pick one of each while the answer could only ever honour one.
 * Migration 0059 merged them. What is left is a single record with a single
 * form, and the sub-tabs are that record's facets rather than two objects
 * sharing a strip.
 *
 * Deleting is confirmed inside the panel rather than here: on this screen an
 * agent is deployment furniture being maintained, and the row being removed
 * may well be one somebody else is chatting with.
 */
export function AgentsAdminTab(): JSX.Element {
  const { atLeast } = useAccess();
  // Mirrors what the API classifies the agent-admin routes as: settings.read
  // opens them, settings.update writes. This tab is also reachable on
  // `copilot.mcp_integrations_manage` alone, and for that principal every
  // agent request would 403 — so the screen says so rather than offering
  // controls that cannot work.
  const canSeeAgents = atLeast("settings", "view");
  const canEditAgents = atLeast("settings", "edit");

  const [tab, setTab] = useState<AgentTab>("profile");
  const roster = useAgentRoster(canSeeAgents);

  if (!canSeeAgents) {
    return (
      <section className="set-section" data-testid="agents-admin-tab">
        <div className="set-list set-list-empty">
          <p className="field-error" role="alert">
            You do not have permission to manage agents.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="set-section" data-testid="agents-admin-tab">
      <div className="agents-layout" data-testid="agents-admin">
        <AgentRosterList roster={roster} />
        <section className="agents-workspace">
          <nav className="agents-tabs" role="tablist" data-testid="agents-subtabs">
            {AGENT_TAB_IDS.map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                className={tab === id ? "active" : ""}
                // Setup doubles as the create form, so it is reachable with
                // nothing selected. The rest edit a specific agent and would
                // have nothing to show.
                disabled={!roster.selected && id !== "profile"}
                onClick={() => setTab(id)}
              >
                {AGENT_TAB_LABELS[id]}
              </button>
            ))}
          </nav>
          <AgentsAdminPanel roster={roster} tab={tab} canEdit={canEditAgents} />
        </section>
      </div>
    </section>
  );
}
